/**
 * Zoom matiere de la grille de la page Collection.
 *
 * Complement, jamais l'essentiel : la grille montre des tabourets habilles, ce
 * zoom repond a la seule question qu'une vignette de 300px ne peut pas trancher
 * — le grain, entre deux teintes voisines. Il n'ouvre donc que la matiere, pas
 * le tabouret, et ne change rien a la selection.
 *
 * Les loupes sont rendues avec `hidden` et c'est ce composant qui les revele.
 * Sans JS il n'y a pas de visionneuse a ouvrir, et le lien vers la fiche produit
 * reste la reponse juste : mieux vaut pas de bouton qu'un bouton mort.
 *
 * Meme montage que `<go-context-photos>` : une seule `<modal-dialog>` de Dawn,
 * dont l'image est remplacee a chaque ouverture.
 */
class GoTextureZoom extends HTMLElement {
  connectedCallback() {
    /*
     * ⚠ Le modal se cherche PAR ID SUR LE DOCUMENT, jamais dans cet element.
     *
     * `ModalDialog.connectedCallback` (global.js) se termine par
     * `document.body.appendChild(this)` : le modal a donc DEJA quitte
     * `<go-texture-zoom>` quand ce code s'execute. Les deux scripts sont
     * differes, `global.js` est dans le `<head>` et celui-ci dans le corps de la
     * section — l'ordre du document decide, et il est toujours dans ce sens.
     * Un `this.querySelector('modal-dialog')` renvoie donc `null`, et la
     * visionneuse ne s'ouvre jamais, sans erreur visible.
     *
     * C'est l'idiome de Dawn lui-meme (`product-info.js` :
     * `document.querySelector('#ProductModal-' + section)`). Le repli sur la
     * recherche locale couvre le cas ou le modal n'a pas ete deplace, faute
     * d'upgrade.
     */
    this.modal =
      document.getElementById(`GoZoomModal-${this.dataset.section}`) || this.querySelector('modal-dialog');

    // Le plateau et la legende ont voyage AVEC le modal : les chercher ici
    // reproduirait le meme piege un cran plus bas.
    this.stage = this.modal?.querySelector('[data-zoom-stage]');
    this.caption = this.modal?.querySelector('[data-zoom-caption]');
    this.buttons = Array.from(this.querySelectorAll('[data-zoom-value]'));
    if (!this.modal || !this.stage || this.buttons.length === 0) return;

    try {
      this.data = JSON.parse(this.querySelector('[data-zoom-data]')?.textContent || '{}');
    } catch {
      this.data = {};
    }

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);

    // Une loupe sans entree dans la table n'ouvrirait rien : elle reste masquee.
    this.buttons.forEach((button) => {
      if (this.data[button.dataset.zoomValue]) button.hidden = false;
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const button = event.target.closest('[data-zoom-value]');
    if (!button || !this.buttons.includes(button)) return;

    const entry = this.data[button.dataset.zoomValue];
    if (!entry) return;

    // L'image est recreee a chaque ouverture : reutiliser le meme noeud
    // laisserait la matiere precedente a l'ecran pendant le chargement de la
    // nouvelle — precisement la confusion que ce zoom doit lever.
    const image = new Image();
    image.className = 'go-zoom__image';
    image.alt = '';
    image.src = entry.u;
    // `c` dit que la source est le pack-shot du tabouret entier, faute de gros
    // plan : on entre alors dedans jusqu'a l'assise. Le cadrage est en CSS, la
    // classe ne fait que dire lequel des deux cas s'applique.
    if (entry.c) image.classList.add('go-zoom__image--crop');
    this.stage.replaceChildren(image);

    if (this.caption) this.caption.textContent = entry.n || '';

    // `show(opener)` rend le focus a la loupe a la fermeture : c'est le contrat
    // de <modal-dialog>, on ne le contourne pas.
    this.modal.show(button);
  }
}

// Meme garde que les autres composants GO! : deux balises <script src>
// identiques sur une page s'executent deux fois, et le second passage
// redefinirait un nom deja pris.
if (!customElements.get('go-texture-zoom')) {
  customElements.define('go-texture-zoom', GoTextureZoom);
}
